import { defaultFetchLovBatch } from './default-fetch'
import {
  buildLovCacheKey,
  clearPendingLov,
  getLovFromCache,
  getPendingLov,
  setLovCache,
  setPendingLov,
} from './lov-store'

import type { LovBatchFetchFn, LovFetchFn, LovOption } from './types'

const DEFAULT_CACHE_TTL = 5 * 60 * 1000

const batchPending = new Map<string, Promise<Record<string, LovOption[]>>>()

function buildBatchRequestKey(types: string[]): string {
  return types.slice().sort().join('\0')
}

export interface LoadLovBatchOptions {
  fetchBatch?: LovBatchFetchFn
  fetchLov?: LovFetchFn
  cacheTtl?: number
}

/** 批量拉取值集并写入内存缓存（同批 types 去重、并发复用） */
export async function loadLovBatch(
  types: string[],
  options: LoadLovBatchOptions = {}
): Promise<void> {
  const ttl =
    options.cacheTtl === undefined ? DEFAULT_CACHE_TTL : options.cacheTtl
  const unique = [...new Set(types.filter(Boolean))]
  if (!unique.length) {
    return
  }

  const missing = unique.filter((type) => {
    const cacheKey = buildLovCacheKey(type)
    return !getLovFromCache(cacheKey, ttl)
  })
  if (!missing.length) {
    return
  }

  const fetchBatch =
    options.fetchBatch ??
    (options.fetchLov
      ? async (batchTypes: string[]) => {
          const result: Record<string, LovOption[]> = {}
          await Promise.all(
            batchTypes.map(async (type) => {
              result[type] = await options.fetchLov!(type)
            })
          )
          return result
        }
      : defaultFetchLovBatch)
  const batchKey = buildBatchRequestKey(missing)

  let batchPromise = batchPending.get(batchKey)
  if (!batchPromise) {
    batchPromise = fetchBatch(missing).finally(() => {
      batchPending.delete(batchKey)
    })
    batchPending.set(batchKey, batchPromise)
  }

  const result = await batchPromise
  for (const type of missing) {
    const cacheKey = buildLovCacheKey(type)
    if (result[type]) {
      setLovCache(cacheKey, result[type], ttl)
    }
  }
}
